import { CUSTOMER_REVIEW_PROMPT_CTA_LABEL } from './reviewConfig';
import { types as sdkTypes } from './sdkLoader';
import { REVIEW_TYPE_OF_PROVIDER } from './types';

/**
 * Coach → customer review prompt (legacy / disabled by default).
 */
export const isCoachToCustomerReviewRequest = (metadata = {}, bodyText = '') => {
  if (metadata?.kind !== 'review_request') return false;
  if (metadata?.reviewerRole === 'coach') return true;
  const url = String(metadata?.action?.url || '').trim();
  if (url.startsWith('/sale/')) return true;
  const body = String(bodyText || metadata?.body || '').toLowerCase();
  if (body.includes('review for your customer')) return true;
  return false;
};

export const isCustomerToCoachReviewRequest = (metadata = {}, bodyText = '') => {
  if (metadata?.kind !== 'review_request') return false;
  if (metadata?.reviewerRole === 'customer') return true;
  const body = String(bodyText || metadata?.body || '').toLowerCase();
  if (body.includes('review for your coach')) return true;
  const url = String(metadata?.action?.url || metadata?.cta?.path || '').trim();
  if (url.startsWith('/order/')) return true;
  if (url.startsWith('/u/') && url.includes('reviewTransactionId=')) return true;
  if (metadata?.transactionId && (metadata?.coachId || metadata?.revieweeId)) return true;
  return false;
};

/**
 * Resolve inbox review CTA URL (defaults to /order/:transactionId for customer → coach prompts).
 */
export const resolveCustomerReviewRequestActionUrl = (metadata = {}) => {
  const direct = String(metadata?.action?.url || metadata?.cta?.path || '').trim();
  if (direct) return direct;
  const transactionId = metadata?.transactionId;
  if (transactionId) {
    return `/order/${transactionId}`;
  }
  return '';
};

/**
 * Whether the review_request message should be hidden from the thread entirely.
 */
export const shouldHideReviewRequestMessage = ({
  metadata = {},
  bodyText = '',
  currentUserId = '',
} = {}) => {
  if (metadata?.kind !== 'review_request') return false;
  if (metadata?.hidden || metadata?.dismissed) return true;

  if (isCoachToCustomerReviewRequest(metadata, bodyText)) {
    return String(metadata?.reviewerId || '') !== String(currentUserId || '');
  }

  if (isCustomerToCoachReviewRequest(metadata, bodyText)) {
    return String(metadata?.reviewerId || '') !== String(currentUserId || '');
  }

  return true;
};

/**
 * Show the customer review CTA card in inbox / thread.
 */
export const shouldShowCustomerReviewRequestCard = ({
  metadata = {},
  bodyText = '',
  currentUserId = '',
} = {}) => {
  if (shouldHideReviewRequestMessage({ metadata, bodyText, currentUserId })) return false;
  if (!isCustomerToCoachReviewRequest(metadata, bodyText)) return false;
  if (String(metadata?.reviewerId || '') !== String(currentUserId || '')) return false;
  return Boolean(resolveCustomerReviewRequestActionUrl(metadata));
};

export const resolveReviewRequestActionUrl = metadata =>
  resolveCustomerReviewRequestActionUrl(metadata);

export const shouldShowReviewRequestCard = ({
  metadata = {},
  bodyText = '',
  currentUserId = '',
} = {}) => {
  if (shouldHideReviewRequestMessage({ metadata, bodyText, currentUserId })) return false;
  if (
    !isCustomerToCoachReviewRequest(metadata, bodyText) &&
    !isCoachToCustomerReviewRequest(metadata, bodyText)
  ) {
    return false;
  }
  if (String(metadata?.reviewerId || '') !== String(currentUserId || '')) return false;
  return Boolean(resolveReviewRequestActionUrl(metadata));
};

export const normalizeCustomerReviewRequestMetadata = (metadata = {}) => {
  const action = metadata?.action || {};
  const resolvedUrl = resolveCustomerReviewRequestActionUrl(metadata);
  return {
    ...metadata,
    action: {
      ...action,
      url: action.url || resolvedUrl,
      label: action.label || metadata?.cta?.label || CUSTOMER_REVIEW_PROMPT_CTA_LABEL,
    },
  };
};

export const normalizeReviewRequestMetadata = metadata =>
  normalizeCustomerReviewRequestMetadata(metadata);

/**
 * Round average rating to nearest 0.5 for display (Sharetribe stores integer 1–5 per review).
 */
export const roundRatingToHalf = value => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 2) / 2;
};

export const formatAverageRating = (value, { empty = '0.0' } = {}) => {
  const rounded = roundRatingToHalf(value);
  if (!rounded) return empty;
  return Number.isInteger(rounded) ? rounded.toFixed(1) : rounded.toFixed(1);
};

export const computeAverageRating = (reviews = []) => {
  if (!reviews?.length) return { average: 0, averageDisplay: '0.0', count: 0 };
  const sum = reviews.reduce((acc, r) => acc + (Number(r?.attributes?.rating ?? r?.rating) || 0), 0);
  const average = sum / reviews.length;
  return {
    average,
    averageDisplay: formatAverageRating(average),
    count: reviews.length,
    averageRounded: roundRatingToHalf(average),
  };
};

/**
 * Sharetribe reviews.query expects subject_id as an SDK UUID instance.
 */
export const normalizeReviewSubjectId = subjectId => {
  if (!subjectId) return null;
  if (subjectId instanceof sdkTypes.UUID) return subjectId;
  if (typeof subjectId === 'string') return new sdkTypes.UUID(subjectId);
  if (typeof subjectId?.uuid === 'string') return new sdkTypes.UUID(subjectId.uuid);
  return null;
};

/** Customer → coach reviews shown on public coach profiles (published ofProvider only). */
export const filterCoachProfilePublicReviews = (reviews = []) =>
  (reviews || []).filter(review => review?.attributes?.type === REVIEW_TYPE_OF_PROVIDER);

export const sortReviewsNewestFirst = (reviews = []) =>
  [...(reviews || [])].sort(
    (a, b) =>
      new Date(b?.attributes?.createdAt || 0).getTime() -
      new Date(a?.attributes?.createdAt || 0).getTime()
  );
